import React, { Component } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
    Avatar,
    Button,
    CssBaseline,
    TextField,
    Grid,
    Typography,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    IconButton,
    Switch,
    FormControlLabel,
    Chip,
    Box,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WebhookIcon from '@mui/icons-material/Webhook';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SendIcon from '@mui/icons-material/Send';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import { Overlay } from '../../styles/global';
import Api from '../../services/api';
import * as WebhookService from '../../services/webhook.service';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import { connect } from 'react-redux';
import Footer from '../../components/footer';
import { browserHistory } from '../../helpers/history';
import { FormattedMessage, injectIntl } from 'react-intl';
import Title from '../../components/title-name';
import WebhookHeadersEditor from '../../components/webhook-headers-editor';
import WebhookAuthConfig from '../../components/webhook-auth-config';
import WebhookEventsSelector from '../../components/webhook-events-selector';
import WebhookTestModal from '../../components/webhook-test-modal';

const SIGNING_ALGORITHMS = ['HMACSHA256', 'HMACSHA512'];

class WebhookEdit extends Component {

    constructor(props) {
        super(props);

        const { userSession } = this.props;

        if (userSession == null || userSession.user == null) {
            Api.logoff();
        }

        this.initialState = {
            id: null,
            guid: null,
            name: '',
            description: '',
            endpointUrl: '',
            httpMethod: 'POST',
            requestFormat: 'JSON',
            authType: 'NONE',
            authConfig: {},
            signingSecret: '',
            signingAlgorithm: 'HMACSHA256',
            customHeaders: [],
            isActive: true,
            isPaused: false,
            pauseReason: '',
            rateLimitPerMinute: 60,
            maxRetryAttempts: 5,
            initialRetryIntervalSeconds: 60,
            totalSuccessCount: 0,
            totalFailureCount: 0,
            consecutiveFailures: 0,
            lastTriggeredAt: null,
            lastSuccessAt: null,
            lastFailureAt: null
        };

        this.state = {
            item: this.initialState,
            loading: false,
            openNotification: false,
            notificationVariant: "error",
            notificationMessage: "",
            eventTypes: [],
            selectedEventIds: [],
            subscriptions: [], // Full subscription data for payload config
            showSecret: false,
            testModalOpen: false,
            expandedSections: {
                basic: true,
                authentication: false,
                headers: false,
                security: false,
                retry: false,
                events: true,
                metrics: false
            }
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.closeNotification = this.closeNotification.bind(this);
        this.handleAuthChange = this.handleAuthChange.bind(this);
        this.handleHeadersChange = this.handleHeadersChange.bind(this);
        this.handleEventsChange = this.handleEventsChange.bind(this);
        this.generateSecret = this.generateSecret.bind(this);
    }

    componentDidMount() {
        this.loadWebhook();
        this.loadEventTypes();
    }

    async loadWebhook() {
        // Obter ID da URL ou do redux (para compatibilidade)
        let webhookId = this.props.match?.params?.id;

        if (!webhookId && this.props.webhooks?.webhook?.id) {
            webhookId = this.props.webhooks.webhook.id;
        }

        if (!webhookId) {
            browserHistory.push(`/${this.props.match.params.lang}/webhooks`);
            return;
        }

        this.setState({ loading: true });

        const result = await WebhookService.getWebhookById(webhookId);

        if (result && result.success) {
            const webhook = result.data;

            // Helper para acessar campos case-insensitive
            const getField = (obj, ...fields) => {
                if (!obj) return undefined;
                for (const field of fields) {
                    if (obj[field] !== undefined) return obj[field];
                    if (obj[field.toLowerCase()] !== undefined) return obj[field.toLowerCase()];
                    if (obj[field.charAt(0).toUpperCase() + field.slice(1)] !== undefined)
                        return obj[field.charAt(0).toUpperCase() + field.slice(1)];
                }
                return undefined;
            };

            // Parse customHeaders - backend retorna {key: value}, frontend espera [{key, value}]
            let customHeaders = [];
            const customHeadersRaw = getField(webhook, 'customHeaders', 'CustomHeaders');
            if (customHeadersRaw) {
                try {
                    // Se for string JSON, parsear primeiro
                    const parsed = typeof customHeadersRaw === 'string'
                        ? JSON.parse(customHeadersRaw)
                        : customHeadersRaw;

                    // Verificar se é array ou objeto
                    if (Array.isArray(parsed)) {
                        // Já está no formato [{key, value}]
                        customHeaders = parsed;
                    } else if (typeof parsed === 'object' && parsed !== null) {
                        // É um objeto {key: value} - converter para array
                        customHeaders = Object.entries(parsed).map(([key, value]) => ({
                            key,
                            value: value || ''
                        }));
                    }
                } catch (e) {
                    customHeaders = [];
                }
            }

            // Parse authConfig se for string
            let authConfig = {};
            const authConfigRaw = getField(webhook, 'authConfig', 'AuthConfig');
            if (authConfigRaw) {
                try {
                    authConfig = typeof authConfigRaw === 'string'
                        ? JSON.parse(authConfigRaw)
                        : authConfigRaw;
                } catch (e) {
                    authConfig = {};
                }
            }

            const webhookId = getField(webhook, 'id', 'Id');

            // Verificar se tem auth e signing secret configurados
            const hasAuthConfig = getField(webhook, 'hasAuthConfig', 'HasAuthConfig');
            const hasSigningSecret = getField(webhook, 'hasSigningSecret', 'HasSigningSecret');
            const authType = getField(webhook, 'authType', 'AuthType') || 'NONE';

            // Se tem auth configurado mas não temos os dados, criar objeto placeholder
            if (hasAuthConfig && Object.keys(authConfig).length === 0 && authType !== 'NONE') {
                authConfig = { _hasConfig: true }; // Indicador interno que tem config
            }

            this.setState({
                loading: false,
                item: {
                    id: webhookId,
                    guid: getField(webhook, 'guid', 'Guid') || '',
                    name: getField(webhook, 'name', 'Name') || '',
                    description: getField(webhook, 'description', 'Description') || '',
                    endpointUrl: getField(webhook, 'endpointUrl', 'EndpointUrl') || '',
                    httpMethod: getField(webhook, 'httpMethod', 'HttpMethod') || 'POST',
                    requestFormat: getField(webhook, 'requestFormat', 'RequestFormat') || 'JSON',
                    authType: authType,
                    authConfig: authConfig,
                    hasAuthConfig: hasAuthConfig,
                    signingSecret: '', // Nao retornamos o secret por seguranca
                    hasSigningSecret: hasSigningSecret,
                    signingAlgorithm: getField(webhook, 'signingAlgorithm', 'SigningAlgorithm') || 'HMACSHA256',
                    customHeaders: customHeaders,
                    isActive: getField(webhook, 'isActive', 'IsActive') ?? true,
                    isPaused: getField(webhook, 'isPaused', 'IsPaused') ?? false,
                    pauseReason: getField(webhook, 'pauseReason', 'PauseReason') || '',
                    rateLimitPerMinute: getField(webhook, 'rateLimitPerMinute', 'RateLimitPerMinute') || 60,
                    maxRetryAttempts: getField(webhook, 'maxRetryAttempts', 'MaxRetryAttempts') || 5,
                    initialRetryIntervalSeconds: getField(webhook, 'initialRetryIntervalSeconds', 'InitialRetryIntervalSeconds') || 60,
                    totalSuccessCount: getField(webhook, 'totalSuccessCount', 'TotalSuccessCount') || 0,
                    totalFailureCount: getField(webhook, 'totalFailureCount', 'TotalFailureCount') || 0,
                    consecutiveFailures: getField(webhook, 'consecutiveFailures', 'ConsecutiveFailures') || 0,
                    lastTriggeredAt: getField(webhook, 'lastTriggeredAt', 'LastTriggeredAt'),
                    lastSuccessAt: getField(webhook, 'lastSuccessAt', 'LastSuccessAt'),
                    lastFailureAt: getField(webhook, 'lastFailureAt', 'LastFailureAt')
                }
            });

            // Carregar subscriptions
            this.loadSubscriptions(webhookId);
        } else {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: result?.errors && result.errors[0] ? result.errors[0] : intl.formatMessage({ id: "process.error" })
            });
        }
    }

    async loadSubscriptions(webhookId) {
        const result = await WebhookService.getWebhookSubscriptions(webhookId);
        if (result && result.success && result.data) {
            const selectedIds = result.data.map(sub => {
                // Case-insensitive access
                return sub.eventTypeId || sub.EventTypeId || sub.eventtypeid;
            }).filter(id => id != null);
            this.setState({
                selectedEventIds: selectedIds,
                subscriptions: result.data // Store full subscription data for payload config
            });
        }
    }

    handleSubscriptionUpdate = (updatedSubscription) => {
        // Update the subscription in the local state
        this.setState(prevState => ({
            subscriptions: prevState.subscriptions.map(sub =>
                (sub.id || sub.Id) === (updatedSubscription.id || updatedSubscription.Id)
                    ? updatedSubscription
                    : sub
            )
        }));
    }

    async loadEventTypes() {
        const result = await WebhookService.getEventTypes();
        if (result && result.success) {
            this.setState({ eventTypes: result.data });
        } else if (result) {
            const intl = this.props.intl;
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: result?.errors && result.errors[0] ? result.errors[0] : intl.formatMessage({ id: "process.error" })
            });
        }
    }

    handleInputChange(e) {
        const name = e.target.name;
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

        this.setState(prevState => ({
            item: {
                ...prevState.item,
                [name]: value
            }
        }));
    }

    handleAuthChange(field, value) {
        this.setState(prevState => ({
            item: {
                ...prevState.item,
                [field]: value
            }
        }));
    }

    handleHeadersChange(headers) {
        this.setState(prevState => ({
            item: {
                ...prevState.item,
                customHeaders: headers
            }
        }));
    }

    handleEventsChange(selectedIds) {
        this.setState({ selectedEventIds: selectedIds });
    }

    async generateSecret() {
        const { item } = this.state;

        if (item.id) {
            // Webhook existente - usar API para regenerar
            this.setState({ loading: true });
            const result = await WebhookService.regenerateWebhookSecret(item.id);
            if (result.success) {
                this.setState(prevState => ({
                    loading: false,
                    item: {
                        ...prevState.item,
                        signingSecret: result.data.newSecret
                    },
                    openNotification: true,
                    notificationVariant: "success",
                    notificationMessage: this.props.intl.formatMessage({ id: "webhook.secret.regenerated" })
                }));
            } else {
                this.setState({
                    loading: false,
                    openNotification: true,
                    notificationVariant: "error",
                    notificationMessage: result.errors && result.errors[0] ? result.errors[0] : this.props.intl.formatMessage({ id: "process.error" })
                });
            }
        } else {
            // Novo webhook - gerar localmente
            const secret = WebhookService.generateLocalSecret();
            this.setState(prevState => ({
                item: {
                    ...prevState.item,
                    signingSecret: secret
                }
            }));
        }
    }

    toggleSection = (section) => {
        this.setState(prevState => ({
            expandedSections: {
                ...prevState.expandedSections,
                [section]: !prevState.expandedSections[section]
            }
        }));
    }

    validateForm() {
        const { item, selectedEventIds } = this.state;
        const intl = this.props.intl;

        if (!item.name.trim()) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.name.required" })
            });
            return false;
        }

        if (!item.endpointUrl.trim()) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.url.required" })
            });
            return false;
        }

        // Validar URL
        try {
            new URL(item.endpointUrl);
        } catch (e) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.url.invalid" })
            });
            return false;
        }

        if (selectedEventIds.length === 0) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.events.required" })
            });
            return false;
        }

        return true;
    }

    handleSubmit = async e => {
        e.preventDefault();

        if (!this.validateForm()) return;

        this.setState({ loading: true });

        const { item, selectedEventIds } = this.state;

        // Preparar authConfig - enviar objeto, não string
        let authConfigObj = null;
        if (item.authType !== 'NONE' && item.authConfig && Object.keys(item.authConfig).length > 0) {
            // Remover indicador interno _hasConfig se existir
            const { _hasConfig, ...cleanAuthConfig } = item.authConfig;
            // Só envia se tiver dados reais (não apenas o indicador)
            if (Object.keys(cleanAuthConfig).length > 0) {
                authConfigObj = cleanAuthConfig;
            }
        }

        // Preparar customHeaders - converter de array [{key, value}] para objeto {key: value}
        let customHeadersObj = null;
        if (item.customHeaders && item.customHeaders.length > 0) {
            customHeadersObj = {};
            item.customHeaders.forEach(h => {
                if (h.key && h.key.trim()) {
                    customHeadersObj[h.key] = h.value || '';
                }
            });
            // Se ficou vazio, manter null
            if (Object.keys(customHeadersObj).length === 0) {
                customHeadersObj = null;
            }
        }

        const webhookData = {
            name: item.name,
            description: item.description,
            endpointUrl: item.endpointUrl,
            httpMethod: item.httpMethod,
            requestFormat: item.requestFormat,
            authType: item.authType,
            authConfig: authConfigObj,
            signingSecret: item.signingSecret || null,
            signingAlgorithm: item.signingAlgorithm,
            customHeaders: customHeadersObj,
            isActive: item.isActive,
            rateLimitPerMinute: parseInt(item.rateLimitPerMinute),
            maxRetryAttempts: parseInt(item.maxRetryAttempts),
            initialRetryIntervalSeconds: parseInt(item.initialRetryIntervalSeconds),
            eventTypeIds: selectedEventIds
        };

        const result = await WebhookService.updateWebhook(item.id, webhookData);

        if (result.success) {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "success",
                notificationMessage: intl.formatMessage({ id: "webhook.updated.success" })
            });
        } else {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: 'error',
                notificationMessage: result.errors && result.errors[0] ? result.errors[0] : intl.formatMessage({ id: "process.error" })
            });
        }
    };

    closeNotification() {
        this.setState({ openNotification: false });
    }

    formatDate(dateString) {
        if (!dateString) return '-';
        const intl = this.props.intl;
        const date = new Date(dateString);
        return date.toLocaleString(intl.locale);
    }

    render() {
        const { classes, headerMenu, actionsHeaderMenu } = this.props;
        const { item, expandedSections, eventTypes } = this.state;
        const intl = this.props.intl;

        if (Api == null) {
            var l = this.props.match.params.lang;
            document.location.href = `/${l}/unavailable`;
        }

        return (
            <div className={classes.root}>
                <HeaderMenu />
                <main
                    className={clsx(classes.content, {
                        [classes.contentShift]: headerMenu.open,
                    }, classes.actionsContent, {
                        [classes.actionscontentShift]: actionsHeaderMenu.open,
                    })}
                >
                    <div className={classes.drawerHeader} />

                    <Container component="main" maxWidth="md">
                        <CssBaseline />
                        <div className={classes.paper} style={{ marginTop: 0 }}>
                            <Grid container spacing={6}>
                                <Grid item xs={12} sm={4}>
                                    <Button variant="outlined" color="primary" onClick={() => browserHistory.push(`/${this.props.match.params.lang}/webhooks`)}>
                                        {<FormattedMessage id="back" />}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Avatar className={classes.avatar}>
                                <WebhookIcon />
                            </Avatar>
                            <Typography component="h1" variant="h5">
                                {<FormattedMessage id="webhook.edit" />}
                                <Title />
                            </Typography>

                            {/* Status badges */}
                            <Box display="flex" gap={1} mt={2} mb={2}>
                                <Chip
                                    label={item.isActive ? intl.formatMessage({ id: "active" }) : intl.formatMessage({ id: "inactive" })}
                                    color={item.isActive ? "success" : "default"}
                                    size="small"
                                />
                                {item.isPaused && (
                                    <Chip
                                        label={intl.formatMessage({ id: "paused" })}
                                        color="warning"
                                        size="small"
                                    />
                                )}
                            </Box>

                            <form name="webhookForm" className={classes.form} onSubmit={this.handleSubmit}>
                                {/* Informacoes Basicas */}
                                <Accordion expanded={expandedSections.basic} onChange={() => this.toggleSection('basic')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.basic" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="name"
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "name" })}
                                                    value={item.name}
                                                    onChange={this.handleInputChange}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="description"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    label={intl.formatMessage({ id: "description" })}
                                                    value={item.description}
                                                    onChange={this.handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="endpointUrl"
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.endpoint.url" })}
                                                    value={item.endpointUrl}
                                                    onChange={this.handleInputChange}
                                                    placeholder="https://api.exemplo.com/webhook"
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.http.method" })}
                                                    value="POST"
                                                    disabled
                                                    helperText={intl.formatMessage({ id: "webhook.http.method.fixed" })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.request.format" })}
                                                    value="JSON"
                                                    disabled
                                                    helperText={intl.formatMessage({ id: "webhook.request.format.fixed" })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            name="isActive"
                                                            checked={item.isActive}
                                                            onChange={this.handleInputChange}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={intl.formatMessage({ id: "webhook.active" })}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Autenticacao */}
                                <Accordion expanded={expandedSections.authentication} onChange={() => this.toggleSection('authentication')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.authentication" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookAuthConfig
                                            authType={item.authType}
                                            authConfig={item.authConfig}
                                            onChange={this.handleAuthChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Headers */}
                                <Accordion expanded={expandedSections.headers} onChange={() => this.toggleSection('headers')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.headers" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookHeadersEditor
                                            headers={item.customHeaders}
                                            onChange={this.handleHeadersChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Seguranca */}
                                <Accordion expanded={expandedSections.security} onChange={() => this.toggleSection('security')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.security" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.signing.secret.note" })}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <TextField
                                                    name="signingSecret"
                                                    variant="outlined"
                                                    fullWidth
                                                    type={this.state.showSecret ? 'text' : 'password'}
                                                    label={intl.formatMessage({ id: "webhook.signing.secret" })}
                                                    value={item.signingSecret}
                                                    onChange={this.handleInputChange}
                                                    placeholder={intl.formatMessage({ id: "webhook.signing.secret.placeholder" })}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => this.setState({ showSecret: !this.state.showSecret })}
                                                                    edge="end"
                                                                >
                                                                    {this.state.showSecret ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    startIcon={<AutorenewIcon />}
                                                    onClick={this.generateSecret}
                                                    style={{ height: '56px' }}
                                                >
                                                    {intl.formatMessage({ id: "webhook.generate.secret" })}
                                                </Button>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>{intl.formatMessage({ id: "webhook.signing.algorithm" })}</InputLabel>
                                                    <Select
                                                        name="signingAlgorithm"
                                                        value={item.signingAlgorithm}
                                                        onChange={this.handleInputChange}
                                                        label={intl.formatMessage({ id: "webhook.signing.algorithm" })}
                                                    >
                                                        {SIGNING_ALGORITHMS.map(alg => (
                                                            <MenuItem key={alg} value={alg}>{alg}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Retry e Rate Limit */}
                                <Accordion expanded={expandedSections.retry} onChange={() => this.toggleSection('retry')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.retry" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="maxRetryAttempts"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.max.retry" })}
                                                    value={item.maxRetryAttempts}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 0, max: 10 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="initialRetryIntervalSeconds"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.initial.retry.interval" })}
                                                    value={item.initialRetryIntervalSeconds}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 1 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="rateLimitPerMinute"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.rate.limit" })}
                                                    value={item.rateLimitPerMinute}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 1 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Eventos */}
                                <Accordion expanded={expandedSections.events} onChange={() => this.toggleSection('events')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.events" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookEventsSelector
                                            eventTypes={this.state.eventTypes}
                                            selectedEventIds={this.state.selectedEventIds}
                                            onChange={this.handleEventsChange}
                                            webhookId={item.id}
                                            subscriptions={this.state.subscriptions}
                                            onSubscriptionUpdate={this.handleSubscriptionUpdate}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Metricas */}
                                <Accordion expanded={expandedSections.metrics} onChange={() => this.toggleSection('metrics')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.metrics" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.total.success" })}
                                                </Typography>
                                                <Typography variant="h6" color="success.main">
                                                    {item.totalSuccessCount}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.total.failure" })}
                                                </Typography>
                                                <Typography variant="h6" color="error.main">
                                                    {item.totalFailureCount}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.consecutive.failures" })}
                                                </Typography>
                                                <Typography variant="h6" color={item.consecutiveFailures > 0 ? "warning.main" : "text.primary"}>
                                                    {item.consecutiveFailures}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Divider style={{ margin: '16px 0' }} />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.last.triggered" })}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {this.formatDate(item.lastTriggeredAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.last.success" })}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {this.formatDate(item.lastSuccessAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {intl.formatMessage({ id: "webhook.last.failure" })}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {this.formatDate(item.lastFailureAt)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Botoes */}
                                <Grid container spacing={2} style={{ marginTop: 24 }} justifyContent="flex-end">
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            fullWidth
                                            variant='outlined'
                                            color='secondary'
                                            startIcon={<SendIcon />}
                                            onClick={() => this.setState({ testModalOpen: true })}
                                        >
                                            {intl.formatMessage({ id: "webhook.test" })}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            fullWidth
                                            variant='outlined'
                                            color='primary'
                                            onClick={() => browserHistory.push(`/${this.props.match.params.lang}/webhooks`)}
                                        >
                                            {<FormattedMessage id="cancel" />}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            type='submit'
                                            fullWidth
                                            variant='contained'
                                            color='primary'
                                        >
                                            {<FormattedMessage id="save" />}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </div>

                        {this.state.loading && (
                            <Overlay>
                                <CircularProgress color="secondary" />
                            </Overlay>
                        )}

                        <CustomizedSnackbars
                            variant={this.state.notificationVariant}
                            message={this.state.notificationMessage}
                            isOpen={this.state.openNotification}
                            toClose={this.closeNotification}
                        />

                        <WebhookTestModal
                            isOpen={this.state.testModalOpen}
                            handleClose={() => this.setState({ testModalOpen: false })}
                            webhookId={item.id}
                            webhookName={item.name}
                            eventTypes={eventTypes}
                            selectedEventIds={this.state.selectedEventIds}
                            subscriptions={this.state.subscriptions}
                            endpointUrl={item.endpointUrl}
                            authType={item.authType}
                            authConfig={item.authConfig}
                            hasAuthConfig={item.hasAuthConfig}
                            customHeaders={item.customHeaders}
                        />
                    </Container>
                </main>
                <Footer />
            </div>
        );
    }
}

WebhookEdit.propTypes = {
    classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
    actionsHeaderMenu: state.actionsHeaderMenu,
    headerMenu: state.headerMenu,
    userSession: state.userSession,
    webhooks: state.webhooks,
});

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(WebhookEdit)));
